import type {
  FileOutbox,
  FileEntry,
  SavedFileEntry,
} from "./file-import-state";
/** Same scoped/transaction-complete persistence pattern as the image outbox.
 * Separate DB leaves existing image recovery records and schema untouched. */
export function createFileOutbox(user: string, room: string): FileOutbox {
  const scope = [user, room];
  let opening: Promise<IDBDatabase> | undefined;
  const open = () =>
    (opening ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open("planroom-file-outbox", 1);
      req.onupgradeneeded = () =>
        req.result
          .createObjectStore("files", { keyPath: "key" })
          .createIndex("scope", "scope");
      req.onerror = () => reject(req.error);
      req.onblocked = () =>
        reject(new Error("Close other Calque tabs and retry."));
      req.onsuccess = () => {
        const db = req.result;
        db.onversionchange = () => {
          db.close();
          opening = undefined;
        };
        resolve(db);
      };
    }).catch((e) => {
      opening = undefined;
      throw e;
    }));
  const transaction = async <T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest<T>,
  ) => {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction("files", mode),
        req = action(tx.objectStore("files"));
      tx.oncomplete = () => resolve(req.result);
      tx.onabort = tx.onerror = () =>
        reject(
          tx.error ??
            req.error ??
            new Error("Cannot save this file on this device."),
        );
    });
  };
  return {
    async list() {
      const db = await open();
      return new Promise<FileEntry[]>((resolve, reject) => {
        const entries: FileEntry[] = [],
          tx = db.transaction("files", "readonly"),
          req = tx
            .objectStore("files")
            .index("scope")
            .openCursor(IDBKeyRange.only(scope));
        req.onsuccess = () => {
          const c = req.result;
          if (!c) return;
          const { file, result, key, scope: unused, ...entry } = c.value;
          entries.push({ ...entry, hasResult: !!result, saved: true });
          c.continue();
        };
        tx.oncomplete = () => resolve(entries);
        tx.onerror = tx.onabort = () => reject(tx.error ?? req.error);
      });
    },
    get: (id) => transaction("readonly", (s) => s.get([...scope, id])),
    async put(entry) {
      await transaction("readwrite", (s) =>
        s.put({ ...entry, key: [...scope, entry.id], scope }),
      );
    },
    async remove(id) {
      await transaction("readwrite", (s) => s.delete([...scope, id]));
    },
  };
}
